import { supabase } from '../lib/supabase';

export const userAddressService = {
  /**
   * Get all addresses for a user
   */
  async getUserAddresses(userId) {
    try {
      const { data, error } = await supabase?.from('user_addresses')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.neq('address_type', 'temporary')
        ?.order('is_default', { ascending: false })
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      return (data || [])?.map(address => ({
        id: address?.id,
        userId: address?.user_id,
        addressType: address?.address_type,
        firstName: address?.first_name,
        lastName: address?.last_name,
        addressLine1: address?.address_line_1,
        addressLine2: address?.address_line_2,
        city: address?.city,
        state: address?.state,
        postalCode: address?.postal_code,
        country: address?.country,
        phone: address?.phone,
        isDefault: address?.is_default,
        createdAt: address?.created_at,
        updatedAt: address?.updated_at
      }));

    } catch (err) {
      console.error('Error fetching user addresses:', err);
      throw err;
    }
  },

  /**
   * Get a specific address by ID
   */
  async getAddressById(addressId) {
    try {
      const { data, error } = await supabase?.from('user_addresses')?.select('*')?.eq('id', addressId)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        addressType: data?.address_type,
        firstName: data?.first_name,
        lastName: data?.last_name,
        addressLine1: data?.address_line_1,
        addressLine2: data?.address_line_2,
        city: data?.city,
        state: data?.state,
        postalCode: data?.postal_code,
        country: data?.country,
        phone: data?.phone,
        isDefault: data?.is_default,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

    } catch (err) {
      console.error('Error fetching address:', err);
      throw err;
    }
  },

  /**
   * Create a new address
   */
  async createAddress(userId, addressData) {
    try {
      // Convert camelCase to snake_case
      const { data, error } = await supabase?.from('user_addresses')?.insert({
          user_id: userId,
          address_type: addressData?.addressType || 'shipping',
          first_name: addressData?.firstName,
          last_name: addressData?.lastName,
          address_line_1: addressData?.addressLine1,
          address_line_2: addressData?.addressLine2 || null,
          city: addressData?.city,
          state: addressData?.state,
          postal_code: addressData?.postalCode,
          country: addressData?.country || 'US',
          phone: addressData?.phone || null,
          is_default: addressData?.isDefault || false
        })?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        addressType: data?.address_type,
        firstName: data?.first_name,
        lastName: data?.last_name,
        addressLine1: data?.address_line_1,
        addressLine2: data?.address_line_2,
        city: data?.city,
        state: data?.state,
        postalCode: data?.postal_code,
        country: data?.country,
        phone: data?.phone,
        isDefault: data?.is_default,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

    } catch (err) {
      console.error('Error creating address:', err);
      throw err;
    }
  },

  /**
   * Update an existing address
   */
  async updateAddress(addressId, addressData) {
    try {
      const { data, error } = await supabase?.from('user_addresses')?.update({
          address_type: addressData?.addressType,
          first_name: addressData?.firstName,
          last_name: addressData?.lastName,
          address_line_1: addressData?.addressLine1,
          address_line_2: addressData?.addressLine2 || null,
          city: addressData?.city,
          state: addressData?.state,
          postal_code: addressData?.postalCode,
          country: addressData?.country,
          phone: addressData?.phone || null,
          is_default: addressData?.isDefault,
          updated_at: new Date()?.toISOString()
        })?.eq('id', addressId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        addressType: data?.address_type,
        firstName: data?.first_name,
        lastName: data?.last_name,
        addressLine1: data?.address_line_1,
        addressLine2: data?.address_line_2,
        city: data?.city,
        state: data?.state,
        postalCode: data?.postal_code,
        country: data?.country,
        phone: data?.phone,
        isDefault: data?.is_default,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

    } catch (err) {
      console.error('Error updating address:', err);
      throw err;
    }
  },

  /**
   * Delete an address
   */
  async deleteAddress(addressId) {
    try {
      const { error } = await supabase?.from('user_addresses')?.delete()?.eq('id', addressId);

      if (error) throw error;

      return true;

    } catch (err) {
      console.error('Error deleting address:', err);
      throw err;
    }
  },

  /**
   * Set an address as default
   */
  async setDefaultAddress(userId, addressId, addressType) {
    try {
      // First, unset all defaults for this type
      await supabase?.from('user_addresses')?.update({ is_default: false })?.eq('user_id', userId)?.eq('address_type', addressType);

      // Then set the new default
      const { data, error } = await supabase?.from('user_addresses')?.update({ is_default: true })?.eq('id', addressId)?.select()?.single();

      if (error) throw error;

      return data;

    } catch (err) {
      console.error('Error setting default address:', err);
      throw err;
    }
  }
};